import { memo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { agentsActions, analyticsActions, channelsActions } from '@actions';
import { CHANNEL } from '@constants/channels';
import { GroupByFilter } from '@constants/analytics';
import { subtractYears } from '@helpers/dates';
import { useChannels, useLocations } from '@hooks';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import { AnalyticsContainer, HeaderWrapper } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import FiltersPanel from './FiltersPanel';
import ConversationsRates from './ConversationsRates';
import ConversationsTable from './ConversationsTable';
import ConversationsTrend from './ConversationsTrend';

const StyledAnalyticsWrapper = styled(AnalyticsWrapper)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const Conversations = memo(() => {
    const firstRender = useRef(true);

    const [filter, setFilter] = useState({
        locations: [],
        channels: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const users = useSelector(state => state.account?.account?.users || []);
    const { data: channels, loading: channelsLoading } = useSelector(state => state.channels);
    const { conversationsData, conversationsLoading } = useSelector(state => state.analytics);
    const language = useSelector(state => state.auth.user?.language);
    const { data: agents } = useSelector(state => state.agents);

    const dispatch = useDispatch();
    const { getChannelsByLocationsIds, checkTwoWaySms, checkWhatsAppStatus } = useChannels();
    const { mapLocationsToFormValue } = useLocations();

    useEffect(() => {
        dispatch(channelsActions.get());
        dispatch(agentsActions.get());
    }, []);

    useEffect(() => {
        if (locations.length && !channelsLoading) {
            const filterCb = c =>
                [CHANNEL.LIVE_CHAT, CHANNEL.FACEBOOK, CHANNEL.INSTAGRAM, CHANNEL.WHATSAPP_BUSINESS].includes(c?.type) ||
                checkTwoWaySms(c) ||
                checkWhatsAppStatus(c);
            const availableChannels = getChannelsByLocationsIds(
                locations.map(l => l.companyId),
                filterCb,
            );
            setFilter(prev => ({
                ...prev,
                channels: [...availableChannels, CHANNEL.ARCHIVED],
                locations: mapLocationsToFormValue(),
            }));
        }
    }, [locations, channelsLoading, channels, dispatch]);

    useEffect(() => {
        if (filter.locations.length && filter.channels.length) {
            if (firstRender.current) {
                onApplyFilters();
                firstRender.current = false;
            }
        }
    }, [filter, dispatch]);

    const onApplyFilters = () => {
        dispatch(
            analyticsActions.getAnalyticsConversations({
                locations: filter.locations.map(item => item.id),
                channels: filter.channels,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                grouping: filter.grouping,
            }),
        );
    };

    return (
        <StyledAnalyticsWrapper>
            <HeaderWrapper>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    filter={filter}
                    setFilter={setFilter}
                    type="conversations"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <PrintButton />
            </HeaderWrapper>
            <AnalyticsContainer id="pl-content-analytics-conversations">
                <ConversationsRates data={conversationsData.rates} isLoading={conversationsLoading} />
                <ConversationsTrend data={conversationsData.trendData} language={language} />
                <ConversationsTable
                    data={conversationsData.tableData}
                    isLoading={conversationsLoading}
                    users={users}
                    agents={agents}
                    filter={filter}
                />
            </AnalyticsContainer>
        </StyledAnalyticsWrapper>
    );
});

Conversations.displayName = 'Conversations';

export default Conversations;
